import { Button } from "@/components/ui/button";
import { Calendar, Camera, Sparkles, TrendingUp, DollarSign, Target, BarChart3, Award, X, ChevronLeft, Handshake, Quote, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import BrandCarousel from "@/components/BrandCarousel";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import FloatingCTA from "@/components/FloatingCTA";
import SEO from "@/components/SEO";
import { createSiteSchema } from "@/lib/structuredData";
import { useSearchParams, Link } from "react-router-dom";
import { getCloudflareImagesVariant, generateResponsiveSrcSet } from "@/lib/imageOptimization";
import { trackBookingCTAClick, trackPortfolioCTAClick } from "@/lib/ga4";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface HeroImage {
  cloudflare_url: string;
  title: string;
  location: string;
}

const Home = () => {
  const { t } = useTranslation();
  const [heroImages, setHeroImages] = useState<HeroImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);

  // Fetch hero images marked with hero_slider=true and use group rotation
  useEffect(() => {
    const fetchHeroImages = async () => {
      const loadImages = async () => {
        try {
          // Fetch only images marked as hero_slider=true
          const { data: heroData, error } = await supabase
            .from('portfolio_images')
            .select(`
              cloudflare_url,
              cloudflare_image_id,
              width,
              height,
              portfolio_items!inner (
                title,
                location
              )
            `)
            .eq('hero_slider', true)
            .eq('is_video', false)
            .not('cloudflare_url', 'is', null)
            .gt('width', 0) // Has dimensions
            .order('cloudflare_image_id', { ascending: true }); // Stable ordering for consistent groups

          if (error || !heroData || heroData.length === 0) {
            console.log('No hero slider images found');
            return;
          }

          // Filter to landscape images only (width > height)
          const landscapeImages = heroData.filter(img => 
            img.width && img.height && img.width > img.height
          );

          if (landscapeImages.length === 0) {
            console.log('No landscape hero images found');
            return;
          }

          console.log(`[Hero] Found ${landscapeImages.length} landscape images marked for hero slider`);

          // Transform to HeroImage format
          const allHeroImages: HeroImage[] = landscapeImages.map(img => ({
            cloudflare_url: img.cloudflare_url!,
            title: (img.portfolio_items as any)?.title || '',
            location: (img.portfolio_items as any)?.location || ''
          }));

          // Create groups of 5 images
          const GROUP_SIZE = 5;
          const groups: HeroImage[][] = [];
          for (let i = 0; i < allHeroImages.length; i += GROUP_SIZE) {
            groups.push(allHeroImages.slice(i, i + GROUP_SIZE));
          }

          if (groups.length === 0) {
            return;
          }

          // Get current group index from localStorage and increment for next visit
          const STORAGE_KEY = 'hero_slider_group_index';
          let groupIndex = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
          
          // Wrap around if needed
          if (groupIndex >= groups.length || isNaN(groupIndex)) {
            groupIndex = 0;
          }

          // Select current group
          const selectedGroup = groups[groupIndex];
          console.log(`[Hero] Using group ${groupIndex + 1}/${groups.length} with ${selectedGroup.length} images`);

          // Store next group index for next visit (round-robin)
          const nextIndex = (groupIndex + 1) % groups.length;
          localStorage.setItem(STORAGE_KEY, String(nextIndex));

          // Set images from selected group
          setHeroImages(selectedGroup);
          setImagesLoaded(Array(selectedGroup.length).fill(false));
        } catch (err) {
          console.error('Error fetching hero images:', err);
        }
      };

      // Defer fetch to after initial paint
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadImages());
      } else {
        setTimeout(loadImages, 100);
      }
    };

    fetchHeroImages();
  }, []);

  // Image slider - first image stays 10s, others 5s
  useEffect(() => {
    if (heroImages.length <= 1) return;

    const interval = currentIndex === 0 ? 10000 : 5000;
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, interval);

    return () => clearTimeout(timer);
  }, [currentIndex, heroImages.length]);

  // Preload next image
  useEffect(() => {
    if (heroImages.length <= 1) return;
    
    const nextIndex = (currentIndex + 1) % heroImages.length;
    if (!imagesLoaded[nextIndex] && heroImages[nextIndex]?.cloudflare_url) {
      const img = new Image();
      img.onload = () => {
        setImagesLoaded(prev => {
          const updated = [...prev];
          updated[nextIndex] = true;
          return updated;
        });
      };
      img.src = getCloudflareImagesVariant(heroImages[nextIndex].cloudflare_url, { width: 1280, quality: 55 });
    }
  }, [currentIndex, heroImages, imagesLoaded]);

  const currentHero = heroImages[currentIndex];
  const heroImageUrl = currentHero?.cloudflare_url 
    ? getCloudflareImagesVariant(currentHero.cloudflare_url, { width: 1280, quality: 55 })
    : '';

  return (
    <>
      <SEO
        title="Leuchtende Hotelfotografie von Thomas Haberland | Hotels im besten Licht"
        description="Professionelle Hotelfotografie: leuchtende Bilder, die Markenwert, Direktbuchungen und Sichtbarkeit steigern."
        path="/"
        locale="de_DE"
        jsonLd={createSiteSchema()}
      />

      <div style={{ marginTop: '-5rem' }}>
        {/* Hero Section */}
        <section 
          className="relative h-screen flex items-center justify-center"
        >
          {/* Hero Images with crossfade */}
          {heroImages.map((hero, index) => (
            <img
              key={hero.cloudflare_url}
              src={index === 0 
                ? getCloudflareImagesVariant(hero.cloudflare_url, { width: 1920, quality: 85 })
                : (imagesLoaded[index] ? getCloudflareImagesVariant(hero.cloudflare_url, { width: 1920, quality: 85 }) : '')
              }
              srcSet={index === 0 ? generateResponsiveSrcSet(hero.cloudflare_url, hero.cloudflare_url, [960, 1280, 1920]) : undefined}
              sizes={index === 0 ? "100vw" : undefined}
              alt={`${hero.title} - ${hero.location} - Leuchtende Hotelfotografie`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
              fetchPriority={index === 0 ? "high" : "low"}
              loading={index === 0 ? "eager" : "lazy"}
              decoding={index === 0 ? "sync" : "async"}
            />
          ))}
          
          <div className="container-page px-4 relative z-10">
            <div className="relative z-10 text-center max-w-4xl mx-auto py-8 md:py-12">
              {isHeroVisible && (
              <div className="bg-black/40 backdrop-blur-md rounded-2xl p-4 sm:p-6 md:p-10 lg:p-12 shadow-2xl relative">
                <button
                  onClick={() => setIsHeroVisible(false)}
                  className="absolute top-3 right-3 md:top-4 md:right-4 text-primary hover:text-primary/80 transition-colors bg-black/30 hover:bg-black/50 rounded-full p-2"
                  aria-label="Close hero section"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
                <h1 className="font-heading font-bold leading-tight text-white mb-6" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
                  {t(`home.hero.variants.0.title`)}
                </h1>
                <p className="hero-subline mb-6 md:mb-8" style={{ fontSize: 'clamp(4rem, 8vw, 6rem)' }}>
                  {t(`home.hero.variants.0.subtitle`)}
                </p>
                <p className="hero-subline--sm mb-6 md:mb-8 lg:mb-12 max-w-2xl mx-auto" style={{ fontSize: 'clamp(3rem, 5vw, 4rem)' }}>
                  {t(`home.hero.variants.0.description`)}
                </p>
                <div className="mt-6 md:mt-8 flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 px-2 sm:px-0">
                  <Button
                    onClick={() => {
                      trackBookingCTAClick({ page: 'home', cta_location: 'hero' });
                      setIsBookingModalOpen(true);
                    }}
                    className="hero-cta font-semibold w-full sm:w-auto"
                  >
                    <Calendar className="h-4 w-4 md:h-5 md:w-5" />
                    <span>{t("home.hero.cta")}</span>
                  </Button>
                  <Link to="/portfolio" onClick={() => trackPortfolioCTAClick({ page: 'home', cta_location: 'hero' })}>
                    <Button className="hero-cta font-semibold w-full sm:w-auto">
                      <Camera className="h-4 w-4 md:h-5 md:w-5" />
                      <span>Portfolio ansehen</span>
                    </Button>
                  </Link>
                </div>
              </div>
              )}
            </div>
          </div>

          {!isHeroVisible && (
            <button
              onClick={() => setIsHeroVisible(true)}
              className="fixed right-6 top-1/2 -translate-y-1/2 z-20 bg-primary text-black rounded-full p-3 shadow-lg"
              aria-label="Reopen hero text"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {/* Slide indicators */}
          {heroImages.length > 1 && (
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${
                    index === currentIndex ? 'bg-primary' : 'bg-white/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-primary' : 'bg-white/50'}`} />
                </button>
              ))}
            </div>
          )}

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-10 border-2 border-primary rounded-full flex items-start justify-center p-2">
              <div className="w-1.5 h-1.5 bg-primary rounded-full" />
            </div>
          </div>
        </section>

        {/* Brand Carousel */}
        <BrandCarousel />

        {/* Features Section */}
        <section className="section bg-card">
          <div className="container-page max-w-6xl">
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center group hover-lift">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="w-8 h-8 text-primary" />
                </div>
                <h3 className="h3 mb-4 text-center text-primary">{t("home.features.technique.title")}</h3>
                <div className="measure mx-auto">
                  <p className="body text-center">
                    {t("home.features.technique.description")}
                  </p>
                </div>
              </div>

              <div className="text-center group hover-lift">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h3 className="h3 mb-4 text-center text-primary">{t("home.features.quality.title")}</h3>
                <div className="measure mx-auto">
                  <p className="body text-center">
                    {t("home.features.quality.description")}
                  </p>
                </div>
              </div>

              <div className="text-center group hover-lift">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Handshake className="w-8 h-8 text-primary" />
                </div>
                <h3 className="h3 mb-4 text-center text-primary">{t("home.features.partner.title")}</h3>
                <div className="measure mx-auto">
                  <p className="body text-center">
                    {t("home.features.partner.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="section relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial opacity-10" />
          <div className="container-page max-w-6xl">
            <div className="relative text-center mb-16">
              <h2 className="h1 mb-6 text-glow text-center">
                {t("home.value.title")}
              </h2>
              <p className="subline-gold mb-8">
                {t("home.value.subtitle")}
              </p>
              <div className="measure mx-auto stack-lg typo">
                <p className="body">
                  {t("home.value.intro1")}
                </p>
                <p className="body">
                  {t("home.value.intro2")}
                </p>
                <p className="body">
                  {t("home.value.intro3")}
                </p>
              </div>
            </div>

            {/* Value Blocks Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <TrendingUp className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.bookings.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.bookings.description")}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <DollarSign className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.rates.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.rates.description")}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.direct.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.direct.description")}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <BarChart3 className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.roi.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.roi.description")}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Award className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.brand.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.brand.description")}
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-8 hover-lift">
                <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="subline-gold--sm mb-4">{t("home.value.blocks.guests.title")}</h3>
                <p className="body text-center">
                  {t("home.value.blocks.guests.description")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <TestimonialCarousel />

        {/* Quote Section */}
        <section className="section-tight bg-card">
          <div className="container-page max-w-3xl text-center">
            <Quote className="w-12 h-12 mx-auto mb-6 text-primary opacity-50" />
            <blockquote className="quote--sm mb-6 text-balance">
              {t("home.quote.text")}
            </blockquote>
            <cite className="caption text-primary not-italic">
              {t("home.quote.author")}
            </cite>
          </div>
        </section>

        {/* Floating CTA */}
        <FloatingCTA />
      </div>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden bg-background p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>Buchen Sie Ihr erstes Beratungsgespräch</DialogTitle>
          </DialogHeader>
          {isBookingModalOpen && (
            <iframe 
              src="https://bookmenow.info/book/MCAL-77c82632-3a93-4622-8307-4d804036002e/ersttermin"
              className="w-full border-0"
              style={{ height: '700px', minWidth: '320px' }}
              title="Buchungskalender"
              allow="payment"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Home;
